import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import {
  Box, Card, Stack, Typography, Button, Chip, CircularProgress, Tooltip,
} from '@mui/material';
import { Link2, SquarePen, Check, X } from 'Utils/lucideIcons';
import { baseUrl } from 'Api';
import { checkReferralKey, changeReferralKey } from 'Api/apiFunctions';
import { AppContext, AuthContext } from 'Context';
import { isNewUIEnabled } from 'hooks/useNewUI';
import { V2SettingsShell } from '../../v2/_shared/V2SettingsShell';
import { ReferralsSettingsClassic } from './ReferralsSettingsClassic';

const rowName = (r) => `${r?.firstname || ''} ${r?.lastname || ''}`.trim() || '—';
const giftCompleted = (g) => g?.plan_id !== null && g?.plan_id !== undefined && `${g?.plan_id}`.trim() !== '';

const REFERRAL_LINK_PREFIX = 'https://www.projectorstream.com/referral/';
const REFERRAL_KEY_FORMAT_MESSAGE = 'Referral key must be 3-50 letters or numbers.';
// Debounce wait before firing the availability check while the user is still typing.
const CODE_CHECK_DEBOUNCE_MS = 500;

// The backend's own message is shown verbatim, but trimmed + capitalized so
// it always reads as a proper sentence regardless of how it's cased upstream.
const formatApiMessage = (message) => {
  const trimmed = (message || '').trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

// Shared viewers-list table styling.
const TH_SX = {
  textAlign: 'left', py: 1, px: 1, fontSize: 12, fontWeight: 700, color: '#9aa1ab', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
};
const TR_SX = { '& td': { py: 1.25, px: 1, fontSize: 14, verticalAlign: 'middle' }, '&:hover td': { bgcolor: '#f8f9fb' } };

const NameCell = ({ name, email }) => (
  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
    <Box sx={{
      width: 32, height: 32, borderRadius: '50%', bgcolor: '#eef1f6', color: '#4b5563', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, textTransform: 'uppercase',
    }}
    >
      {(name || '—').charAt(0)}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#15171c' }} noWrap>{name}</Typography>
      {email && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>{email}</Typography>}
    </Box>
  </Stack>
);

const Table = ({ head, children }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <Box component="thead">
        <Box component="tr" sx={{ '& th': TH_SX }}>{head}</Box>
      </Box>
      <Box component="tbody">{children}</Box>
    </Box>
  </Box>
);

const StateText = ({ children }) => (
  <Typography variant="body2" sx={{ color: 'text.secondary', py: 5, textAlign: 'center' }}>{children}</Typography>
);

const ReferralsSettingsNew = () => {
  const { getToken } = useContext(AuthContext);
  const { setAppSnackbar } = useContext(AppContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [details, setDetails] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('signups');
  const [copied, setCopied] = useState(false);

  const [editingCode, setEditingCode] = useState(false);
  const [codeDraft, setCodeDraft] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);
  const [codeAvailable, setCodeAvailable] = useState(null); // null = not checked yet
  // Verbatim message from the last checkReferralKey/changeReferralKey response —
  // shown as-is so the UI always matches what the backend actually said.
  const [codeCheckMessage, setCodeCheckMessage] = useState(null);
  const [savingCode, setSavingCode] = useState(false);
  const codeCheckTimer = useRef(null);
  const codeCheckSeq = useRef(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const token = await getToken();
        const { data } = await axios.post(`${baseUrl}/getMyReferralDetails`, { token });
        if (!mounted) return;
        if (data?.success) {
          setDetails(data?.referral_details || null);
          setGifts(Array.isArray(data?.gifts) ? data.gifts : []);
          setUsers(Array.isArray(data?.users) ? data.users : []);
        } else {
          setError(true);
        }
      } catch (e) {
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [getToken]);

  const referralKey = details?.referral_key || '';
  const referralLink = referralKey ? `https://www.projectorstream.com/referral/${referralKey}` : '';

  const copyLink = () => {
    if (!referralLink) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(referralLink);
      } else {
        const ta = document.createElement('textarea');
        ta.value = referralLink;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'success', message: 'Referral link copied to clipboard' });
    } catch (e) {
      if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'error', message: 'Could not copy the link' });
    }
  };

  // Format (3-50 letters/numbers) is validated by the backend itself — the
  // checkReferralKey response for bad input is a normal `{ success: true,
  // available: false, message }`, not an error, so it goes through the same
  // debounced call as everything else instead of a client-side shortcut.
  const codeUnchanged = codeDraft === referralKey;
  const codeStatus = (checkingCode || codeUnchanged || !codeDraft)
    ? 'neutral'
    : codeAvailable === false
      ? 'error'
      : codeAvailable === true ? 'success' : 'neutral';
  const CODE_STATUS_BORDER = { neutral: '#d0d5dd', success: '#16a34a', error: '#dc2626' };
  const CODE_STATUS_GLOW = { neutral: 'none', success: '0 0 0 3px rgba(22,163,74,0.14)', error: '0 0 0 3px rgba(220,38,38,0.14)' };

  useEffect(() => {
    if (!editingCode) return undefined;
    codeCheckSeq.current += 1;
    const seq = codeCheckSeq.current;
    if (codeCheckTimer.current) clearTimeout(codeCheckTimer.current);
    setCodeAvailable(null);
    setCodeCheckMessage(null);
    // Your own current key is a documented no-op — skip the network call.
    if (!codeDraft || codeUnchanged) {
      setCheckingCode(false);
      return undefined;
    }
    setCheckingCode(true);
    codeCheckTimer.current = setTimeout(async () => {
      try {
        const { data } = await checkReferralKey({ referral_key: codeDraft });
        if (codeCheckSeq.current !== seq) return; // stale check, input changed since
        setCheckingCode(false);
        if (data?.success) {
          setCodeAvailable(!!data.available);
          setCodeCheckMessage(data.message ? formatApiMessage(data.message) : null);
        } else {
          setCodeAvailable(false);
          setCodeCheckMessage(formatApiMessage(data?.message || 'Could not check availability. Please try again.'));
        }
      } catch (e) {
        if (codeCheckSeq.current !== seq) return; // stale check, input changed since
        setCheckingCode(false);
        setCodeAvailable(false);
        setCodeCheckMessage(formatApiMessage(e?.response?.data?.message || 'Could not check availability. Please try again.'));
      }
    }, CODE_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(codeCheckTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeDraft, editingCode]);

  const startEditCode = () => {
    setCodeDraft(referralKey);
    setCodeAvailable(null);
    setCodeCheckMessage(null);
    setCheckingCode(false);
    setEditingCode(true);
  };

  const cancelEditCode = () => {
    setEditingCode(false);
    setCheckingCode(false);
    setCodeAvailable(null);
    setCodeCheckMessage(null);
    if (codeCheckTimer.current) clearTimeout(codeCheckTimer.current);
  };

  const onCodeDraftChange = (e) => {
    const cleaned = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 50);
    setCodeDraft(cleaned);
  };

  const saveCode = async () => {
    // Re-checks the same conditions the Save button's `disabled` already
    // guards, so a stray Enter keypress can't slip past them either.
    if (savingCode || codeUnchanged || codeAvailable !== true) return;
    setSavingCode(true);
    try {
      const { data } = await changeReferralKey({ referral_key: codeDraft });
      if (data?.success) {
        setDetails((prev) => ({ ...(prev || {}), referral_key: data.referral_key || codeDraft }));
        setEditingCode(false);
        setCodeAvailable(null);
        setCodeCheckMessage(null);
        if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'success', message: data.message || 'Referral key updated.' });
      } else if (setAppSnackbar) {
        setAppSnackbar({ isVisible: true, type: 'error', message: data?.message || 'Could not update referral code' });
      }
    } catch (e) {
      const status = e?.response?.status;
      const message = formatApiMessage(e?.response?.data?.message
        || (status === 409 ? 'Referral key is already taken.' : status === 400 ? REFERRAL_KEY_FORMAT_MESSAGE : 'Could not update referral code. Please try again.'));
      // A 409 means someone else grabbed this exact key between the last
      // availability check and Save — reflect that instead of leaving the
      // (now stale) green "available" state showing.
      if (status === 409) {
        setCodeAvailable(false);
        setCodeCheckMessage(message);
      }
      if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'error', message });
    } finally {
      setSavingCode(false);
    }
  };

  const tabs = [
    { key: 'signups', label: 'Signups', count: users.length },
    { key: 'gifts', label: 'Gifts', count: gifts.length },
  ];

  return (
    <V2SettingsShell
      title="Referrals"
      description="Share your link — people can sign up or receive a gift through it."
    >
      {/* Referral link */}
      {referralKey && (
        <Card sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.6 }}>Your referral link</Typography>

              {editingCode ? (
                <>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      maxWidth: '100%',
                      height: 40,
                      pl: '14px',
                      pr: '10px',
                      boxSizing: 'border-box',
                      bgcolor: '#fff',
                      borderRadius: '999px',
                      border: '1.5px solid',
                      borderColor: CODE_STATUS_BORDER[codeStatus],
                      boxShadow: CODE_STATUS_GLOW[codeStatus],
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      '@keyframes refPillIn': { from: { opacity: 0, transform: 'scale(0.97)' }, to: { opacity: 1, transform: 'scale(1)' } },
                      animation: 'refPillIn 150ms ease',
                    }}
                  >
                    <Link2 size={15} color="#98a2b3" style={{ flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1,
                        }}
                      >
                        {REFERRAL_LINK_PREFIX}
                      </Box>
                      <Box
                        component="input"
                        autoFocus
                        value={codeDraft}
                        onChange={onCodeDraftChange}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveCode();
                          if (e.key === 'Escape') cancelEditCode();
                        }}
                        maxLength={50}
                        aria-label="Referral code"
                        sx={{
                          flexShrink: 0,
                          border: 'none',
                          outline: 'none',
                          background: 'transparent',
                          p: 0,
                          fontFamily: '"Montserrat", sans-serif',
                          fontSize: 14,
                          fontWeight: 700,
                          color: '#15171c',
                          width: `${Math.max(codeDraft.length, 3) + 1}ch`,
                        }}
                      />
                    </Box>
                    <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {checkingCode ? (
                        <CircularProgress size={13} thickness={6} sx={{ color: '#98a2b3' }} />
                      ) : codeAvailable === false ? (
                        <Tooltip title={codeCheckMessage || 'Referral key is already taken.'}>
                          <Box sx={{ display: 'flex' }}><X size={14} color="#dc2626" strokeWidth={3} /></Box>
                        </Tooltip>
                      ) : !codeUnchanged && codeAvailable === true ? (
                        <Tooltip title={codeCheckMessage || 'Referral key is available.'}>
                          <Box sx={{ display: 'flex' }}><Check size={14} color="#16a34a" strokeWidth={3} /></Box>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      disableElevation
                      onClick={saveCode}
                      disabled={savingCode || codeUnchanged || codeAvailable !== true}
                      sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px' }}
                    >
                      {savingCode ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={cancelEditCode}
                      sx={{
                        textTransform: 'none', fontWeight: 700, borderRadius: '999px', color: 'text.secondary', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover', borderColor: 'divider' },
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
                {/* Visible message (not just the icon tooltip) — shows the
                    backend's own wording so bad-format/taken/available all
                    read clearly without hovering. */}
                {(checkingCode || codeCheckMessage) && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.75,
                      fontWeight: 600,
                      color: checkingCode ? 'text.secondary' : codeAvailable === true ? 'success.main' : 'error.main',
                    }}
                  >
                    {checkingCode ? 'Checking availability…' : codeCheckMessage}
                  </Typography>
                )}
                </>
              ) : (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', maxWidth: '100%' }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: 0,
                      boxSizing: 'border-box',
                      height: 40,
                      pl: '14px',
                      pr: '14px',
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '999px',
                    }}
                  >
                    <Link2 size={15} color="#98a2b3" style={{ flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', minWidth: 0 }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 13, color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1,
                        }}
                      >
                        {REFERRAL_LINK_PREFIX}
                      </Box>
                      <Box component="span" sx={{ fontSize: 14, fontWeight: 700, color: '#15171c', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {referralKey}
                      </Box>
                    </Box>
                  </Box>
                  <Tooltip title="Edit referral code">
                    <Box
                      component="button"
                      type="button"
                      onClick={startEditCode}
                      aria-label="Edit referral code"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: '12px',
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': { bgcolor: '#fff', borderColor: '#15171c', color: '#15171c', transform: 'translateY(-1px)', boxShadow: '0 4px 14px rgba(15,17,28,0.08)' },
                      }}
                    >
                      <SquarePen size={16} strokeWidth={2.2} />
                    </Box>
                  </Tooltip>
                </Stack>
              )}
            </Box>

            {!editingCode && (
              <Button
                variant="contained"
                size="small"
                disableElevation
                onClick={copyLink}
                startIcon={copied ? <Check size={15} strokeWidth={2.6} /> : <Link2 size={15} strokeWidth={2.4} />}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px', flexShrink: 0, px: 2 }}
              >
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            )}
          </Stack>
        </Card>
      )}

      {/* Tabbed table — same visual style as the viewers list. */}
      <Card sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5 }}>
        <Stack direction="row" spacing={0.5} sx={{ bgcolor: 'action.hover', p: 0.5, borderRadius: 999, mb: 2, alignSelf: 'flex-start', width: 'fit-content' }}>
          {tabs.map((t) => {
            const active = activeTab === t.key;
            return (
              <Button
                key={t.key}
                size="small"
                disableElevation
                onClick={() => setActiveTab(t.key)}
                variant={active ? 'contained' : 'text'}
                sx={{ borderRadius: 999, minWidth: 0, px: 1.75, textTransform: 'none', fontWeight: 600, color: active ? undefined : 'text.secondary' }}
              >
                {t.label}
                <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontWeight: 700 }}>{t.count}</Box>
              </Button>
            );
          })}
        </Stack>

        {loading ? (
          <StateText>Loading…</StateText>
        ) : error ? (
          <StateText>Couldn&apos;t load your referral details. Please try again later.</StateText>
        ) : activeTab === 'signups' ? (
          users.length === 0 ? (
            <StateText>No one has signed up through your link yet.</StateText>
          ) : (
            <Table head={(
              <>
                <Box component="th">Name</Box>
                <Box component="th">Mobile</Box>
                <Box component="th">Plan</Box>
              </>
            )}
            >
              {users.map((u, idx) => (
                <Box component="tr" key={u?.id || `${u?.email || 'user'}-${idx}`} sx={TR_SX}>
                  <Box component="td"><NameCell name={rowName(u)} email={u?.email} /></Box>
                  <Box component="td"><Typography variant="body2" sx={{ color: 'text.secondary' }}>{u?.mobile || '—'}</Typography></Box>
                  <Box component="td"><Chip size="small" variant="outlined" label={u?.plan_name || 'Free'} sx={{ fontWeight: 600, height: 22 }} /></Box>
                </Box>
              ))}
            </Table>
          )
        ) : gifts.length === 0 ? (
          <StateText>No gifts have been created through your link yet.</StateText>
        ) : (
          <Table head={(
            <>
              <Box component="th">User</Box>
              <Box component="th">From</Box>
              <Box component="th">Promo code</Box>
              <Box component="th">Status</Box>
            </>
          )}
          >
            {gifts.map((g, idx) => {
              const done = giftCompleted(g);
              return (
                <Box component="tr" key={`${g?.email || 'gift'}-${idx}`} sx={TR_SX}>
                  <Box component="td"><NameCell name={rowName(g)} email={g?.email} /></Box>
                  <Box component="td">
                    {(g?.from_name || g?.from_email) ? (
                      <Box>
                        {g?.from_name && <Typography sx={{ fontSize: 14, color: '#15171c' }} noWrap>{g.from_name}</Typography>}
                        {g?.from_email && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>{g.from_email}</Typography>}
                      </Box>
                    ) : '—'}
                  </Box>
                  <Box component="td"><Typography variant="body2" sx={{ color: 'text.secondary' }}>{g?.promo_code || '—'}</Typography></Box>
                  <Box component="td">
                    <Chip size="small" variant="outlined" color={done ? 'success' : 'warning'} label={done ? 'Completed' : 'Incomplete'} sx={{ fontWeight: 600, height: 22 }} />
                  </Box>
                </Box>
              );
            })}
          </Table>
        )}
      </Card>
    </V2SettingsShell>
  );
};

// Classic (old UI) users get the legacy referrals page (classic SettingsPage
// chrome); only new-UI users get the V2SettingsShell version.
export const ReferralsSettings = (props) => (
  isNewUIEnabled() ? <ReferralsSettingsNew {...props} /> : <ReferralsSettingsClassic {...props} />
);

export default ReferralsSettings;
